import CustomText from "../atomic/CustomText";
import Button from "../organism/Button";
import CloseIcon from "../../assets/svg/CloseIcon";
import SubmissionBlockedValidationIcon from "../../assets/svg/SubmissionBlockedValidationIcon";
import BlankDocumentSubmissionBlockedModalProps from "../../services/models/blankDocumentSubmissionBlockedModalProps.model";
import {
  APPROVALS_SUBMISSION_BLOCKED_ISSUE_HEADING,
  APPROVALS_SUBMISSION_BLOCKED_ISSUE_HIGHLIGHT,
  APPROVALS_SUBMISSION_BLOCKED_ISSUE_LINE_AFTER_FILENAME,
  APPROVALS_SUBMISSION_BLOCKED_ISSUE_LINE_OPEN_QUOTE,
  APPROVALS_SUBMISSION_BLOCKED_ISSUE_LINE_SUFFIX,
  APPROVALS_SUBMISSION_BLOCKED_ISSUE_QUOTED_FILE_PLACEHOLDER,
  APPROVALS_SUBMISSION_BLOCKED_MISSING_DESCRIPTION,
  APPROVALS_SUBMISSION_BLOCKED_MISSING_HEADING,
  APPROVALS_SUBMISSION_BLOCKED_REPLACE_FILE,
  APPROVALS_SUBMISSION_BLOCKED_TITLE,
  APPROVALS_SUBMISSION_BLOCKED_VALIDATION_SUBTITLE,
} from "../../constants/approvalsPatientInfoBlockedCopy";

function BlankDocumentSubmissionBlockedModal({
  visible,
  onClose,
  onReplaceFile,
  title = APPROVALS_SUBMISSION_BLOCKED_TITLE,
  validationFailureSubtitle = APPROVALS_SUBMISSION_BLOCKED_VALIDATION_SUBTITLE,
  issueDetectedHeading = APPROVALS_SUBMISSION_BLOCKED_ISSUE_HEADING,
  issueQuotedFileName = APPROVALS_SUBMISSION_BLOCKED_ISSUE_QUOTED_FILE_PLACEHOLDER,
  issueLineOpenQuote = APPROVALS_SUBMISSION_BLOCKED_ISSUE_LINE_OPEN_QUOTE,
  issueLineAfterFilename = APPROVALS_SUBMISSION_BLOCKED_ISSUE_LINE_AFTER_FILENAME,
  issueHighlightText = APPROVALS_SUBMISSION_BLOCKED_ISSUE_HIGHLIGHT,
  issueLineSuffix = APPROVALS_SUBMISSION_BLOCKED_ISSUE_LINE_SUFFIX,
  missingPatientDetailsHeading = APPROVALS_SUBMISSION_BLOCKED_MISSING_HEADING,
  missingPatientDetailsDescription = APPROVALS_SUBMISSION_BLOCKED_MISSING_DESCRIPTION,
  replaceFileButtonText = APPROVALS_SUBMISSION_BLOCKED_REPLACE_FILE,
  issueItems,
}: Readonly<BlankDocumentSubmissionBlockedModalProps>): JSX.Element | null {
  if (!visible) return null;

  const showIssueList = (issueItems?.length ?? 0) > 0;

  return (
    <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-modalBackdrop p-3 tab:items-center tab:p-4 font-sans">
      <div
        className="relative flex max-h-[92vh] w-full max-w-[560px] flex-col overflow-hidden rounded-2xl border border-borderGray bg-white p-4 shadow-md tab:p-6 md:p-8"
        role="dialog"
        aria-modal="true"
        aria-labelledby="approvals-submission-blocked-title"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 p-1 leading-none text-darkGray hover:opacity-70 tab:right-5 tab:top-5 md:right-6 md:top-6"
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <div className="flex flex-col items-center text-center">
          <div
            className="mb-3 flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-[16px] bg-pink tab:mb-4 tab:h-[64px] tab:w-[64px] md:mb-5 md:h-[72px] md:w-[72px] md:rounded-[20px]"
            aria-hidden
          >
            <SubmissionBlockedValidationIcon className="text-requiredRed" width={30} height={30} />
          </div>

          <h2
            id="approvals-submission-blocked-title"
            className="font-sans text-[34px] leading-tight text-royalBlue tab:text-title"
          >
            {title}
          </h2>
          <p className="mt-1 max-w-lg font-sans text-xs font-normal leading-relaxed text-royalBlue tab:mt-2 tab:text-sm">
            {validationFailureSubtitle}
          </p>
        </div>

        <div className="mt-5 w-full space-y-5 overflow-y-auto pr-1 text-left tab:mt-6 tab:space-y-6">
          <section>
            <CustomText
              text={issueDetectedHeading}
              fontWeight="font-semibold"
              fontSize="text-sm"
              className="text-royalBlue mb-2"
            />
            {showIssueList ? (
              <ul className="list-disc space-y-3 pl-5 font-sans text-sm font-normal leading-relaxed text-royalBlue tab:space-y-4 tab:text-base">
                {issueItems!.map((item) => (
                  <li key={item.rowKey ?? `${item.orderLabel}:${item.fileName}`}>
                    <span className="font-medium text-royalBlue">{item.orderLabel}</span>
                    <p className="mt-1 text-royalBlue">
                      <span>{issueLineOpenQuote}</span>
                      <span>{item.fileName}</span>
                      <span>{issueLineAfterFilename}</span>
                      <span className="font-normal text-requiredRed">{issueHighlightText}</span>
                      <span>{issueLineSuffix}</span>
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="font-sans text-sm font-normal leading-relaxed text-royalBlue tab:text-base">
                <span>{issueLineOpenQuote}</span>
                <span>{issueQuotedFileName}</span>
                <span>{issueLineAfterFilename}</span>
                <span className="font-normal text-requiredRed">{issueHighlightText}</span>
                <span>{issueLineSuffix}</span>
              </p>
            )}
          </section>

          <section>
            <CustomText
              text={missingPatientDetailsHeading}
              fontWeight="font-semibold"
              fontSize="text-sm"
              className="text-royalBlue mb-2"
            />
            <CustomText
              text={missingPatientDetailsDescription}
              fontWeight="font-normal"
              fontSize="text-sm tab:text-base"
              className="text-royalBlue leading-relaxed"
            />
          </section>
        </div>

        <div className="mt-5 tab:mt-6">
          <Button
            type="button"
            buttonTheme="primary"
            className="w-full uppercase tracking-wide"
            textInformation={{
              text: replaceFileButtonText,
              fontWeight: "font-semibold",
              fontSize: "text-sm",
            }}
            onClick={onReplaceFile}
          />
        </div>
      </div>
    </div>
  );
}

export default BlankDocumentSubmissionBlockedModal;
